var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// src/global.ts
import { dequal } from "dequal/lite";

// src/devtools.ts
import { addTransformer, stringify, withRecursionTracker } from "ecmason";
addTransformer("object", {
  tag: "PROMISE",
  check: (value) => value instanceof Promise,
  serialize: () => null,
  deserialize: () => Promise.resolve()
});
addTransformer("primitive", {
  tag: "FUNCTION",
  check: (v) => typeof v === "function",
  serialize: (v) => `ƒ ${v.name} () { }`,
  deserialize: (v) => {
    const newFunc = () => {
    };
    newFunc.name = v;
    return newFunc;
  }
});
addTransformer("object", withRecursionTracker({
  tag: "ERROR",
  check: (v) => v instanceof Error,
  serialize: (v) => ({
    name: v.name,
    message: v.message
  }),
  deserialize: (v) => {
    const error = new Error(v.message);
    error.name = v.name;
    return error;
  }
}));
function updateData(key, data) {
  if (process.env.NODE_ENV !== "production" && typeof document !== "undefined") {
    document.dispatchEvent(new CustomEvent("__SWR_STORE__", {
      detail: {
        key,
        data: stringify(data)
      }
    }));
  }
}

// src/cache/reactive-cache.ts
function createReactiveCache() {
  return {
    cache: new Map(),
    subscribers: new Map()
  };
}
function createReactiveCacheRef(cache, key, value) {
  const currentRef = cache.cache.get(key);
  if (currentRef) {
    return currentRef;
  }
  const newRef = {
    value
  };
  cache.cache.set(key, newRef);
  return newRef;
}
function subscribeReactiveCache(cache, key, listener) {
  let subscribers = cache.subscribers.get(key);
  if (!subscribers) {
    subscribers = new Set();
    cache.subscribers.set(key, subscribers);
  }
  subscribers.add(listener);
  return () => {
    subscribers == null ? void 0 : subscribers.delete(listener);
  };
}
function setReactiveCacheValue(cache, key, value, notify = true) {
  const currentRef = createReactiveCacheRef(cache, key, value);
  currentRef.value = value;
  if (notify) {
    let subscribers = cache.subscribers.get(key);
    if (!subscribers) {
      subscribers = new Set();
      cache.subscribers.set(key, subscribers);
    }
    subscribers.forEach((listener) => {
      listener(value);
    });
  }
}
function getReactiveCacheListenerSize(cache, key) {
  var _a, _b;
  return (_b = (_a = cache.subscribers.get(key)) == null ? void 0 : _a.size) != null ? _b : 0;
}

// src/cache/mutation-cache.ts
var MUTATION_CACHE = createReactiveCache();
function subscribeMutation(key, listener) {
  return subscribeReactiveCache(MUTATION_CACHE, key, listener);
}
function setMutation(key, value) {
  setReactiveCacheValue(MUTATION_CACHE, key, value);
  updateData(key, __spreadProps(__spreadValues({}, value), {
    listeners: getReactiveCacheListenerSize(MUTATION_CACHE, key)
  }));
}
function getMutation(key) {
  var _a;
  return (_a = MUTATION_CACHE.cache.get(key)) == null ? void 0 : _a.value;
}
function getMutationListenerSize(key) {
  return getReactiveCacheListenerSize(MUTATION_CACHE, key);
}

// src/cache/revalidation-cache.ts
var REVALIDATION_CACHE = createReactiveCache();
function subscribeRevalidation(key, listener) {
  return subscribeReactiveCache(REVALIDATION_CACHE, key, listener);
}
function setRevalidation(key, value, notify = true) {
  setReactiveCacheValue(REVALIDATION_CACHE, key, value, notify);
}

// src/global.ts
function trigger(key, shouldRevalidate = true) {
  setRevalidation(key, shouldRevalidate);
}
function mutate(key, data, shouldRevalidate = true, compare = dequal) {
  setRevalidation(key, shouldRevalidate);
  const current = getMutation(key);
  if (current && current.result.status === "success" && data.status === "success" && compare(current.result.data, data.data)) {
    current.timestamp = Date.now();
    return;
  }
  setMutation(key, {
    result: data,
    timestamp: Date.now(),
    isValidating: false
  });
}
function subscribe(key, listener) {
  const wrappedListener = (value) => {
    listener(value);
  };
  return subscribeMutation(key, wrappedListener);
}

// src/default-config.ts
import { dequal as dequal2 } from "dequal/lite";
function defaultKey(...args) {
  return JSON.stringify(args);
}
var DEFAULT_CONFIG = {
  revalidateOnFocus: false,
  revalidateOnNetwork: false,
  revalidateOnVisibility: false,
  refreshWhenHidden: false,
  refreshWhenBlurred: false,
  refreshWhenOffline: false,
  freshAge: 2e3,
  staleAge: 3e4,
  key: defaultKey,
  compare: dequal2,
  maxRetryInterval: 5e3
};
var default_config_default = DEFAULT_CONFIG;

// src/is-client.ts
var IS_CLIENT = typeof window !== "undefined" && typeof window.document !== "undefined" && typeof window.document.createElement !== "undefined";
var is_client_default = IS_CLIENT;

// src/never-promise.ts
var NEVER_PROMISE = new Promise(() => {
});
var never_promise_default = NEVER_PROMISE;

// src/retry.ts
function createResolvable() {
  let resolve = () => {
  };
  let reject = () => {
  };
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return {
    promise,
    resolve,
    reject
  };
}
function retry(supplier, options) {
  let alive = true;
  let schedule;
  const resolvable = createResolvable();
  const backoff = (timeout = 10, count = 0) => {
    const handle = (reason) => {
      if (!alive || typeof options.count === "number" && options.count <= count) {
        resolvable.reject(reason);
      } else {
        schedule = window.setTimeout(() => {
          backoff(Math.max(10, Math.min(options.interval, timeout * 2)), count + 1);
        }, timeout);
      }
    };
    try {
      supplier().then(resolvable.resolve, handle);
    } catch (reason) {
      handle(reason);
    }
  };
  backoff();
  return {
    resolvable,
    cancel: () => {
      if (schedule) {
        clearTimeout(schedule);
      }
      alive = false;
    }
  };
}

// src/create-swr-store.ts
var index = 0;
function getIndex() {
  const current = index;
  index += 1;
  return current;
}
var retries = new Map();
function createSWRStore(options) {
  const fullOpts = __spreadValues(__spreadValues({}, default_config_default), options);
  const revalidate = (args, opts) => {
    const defaultRevalidateOptions = {
      shouldRevalidate: true,
      initialData: fullOpts.initialData,
      hydrate: false
    };
    const revalidateOptions = __spreadValues(__spreadValues({}, defaultRevalidateOptions), opts);
    const generatedKey = fullOpts.key(...args);
    const timestamp = Date.now();
    let currentMutation = getMutation(generatedKey);
    if (!currentMutation && revalidateOptions.initialData) {
      currentMutation = {
        result: {
          data: revalidateOptions.initialData,
          status: "success"
        },
        timestamp,
        isValidating: false
      };
      if (revalidateOptions.hydrate) {
        setMutation(generatedKey, currentMutation);
      }
    }
    if (!is_client_default) {
      if (!currentMutation) {
        return {
          status: "pending",
          data: never_promise_default
        };
      }
      return currentMutation.result;
    }
    if (currentMutation) {
      if (!revalidateOptions.shouldRevalidate) {
        return currentMutation.result;
      }
      if (currentMutation.timestamp + fullOpts.freshAge > timestamp) {
        return currentMutation.result;
      }
      if (currentMutation.result.status === "pending") {
        const previousRetry = retries.get(generatedKey);
        if (previousRetry) {
          previousRetry.cancel();
        }
      }
    }
    const pendingRetry = retry(() => fullOpts.get(...args), {
      count: fullOpts.maxRetryCount,
      interval: fullOpts.maxRetryInterval
    });
    retries.set(generatedKey, pendingRetry);
    const pendingData = pendingRetry.resolvable.promise;
    const result = {
      data: pendingData,
      status: "pending"
    };
    pendingData.then((data) => {
      var _a;
      const mutation = getMutation(generatedKey);
      const shouldUpdate = () => {
        if (mutation == null) {
          return true;
        }
        if (mutation.timestamp > timestamp) {
          return false;
        }
        if (mutation.result.status === "success") {
          return !fullOpts.compare(mutation.result.data, data);
        }
        return true;
      };
      if (shouldUpdate()) {
        setMutation(generatedKey, {
          result: {
            data,
            status: "success"
          },
          timestamp: (_a = mutation == null ? void 0 : mutation.timestamp) != null ? _a : Date.now(),
          isValidating: false
        });
      }
    }, (data) => {
      var _a;
      const mutation = getMutation(generatedKey);
      const shouldUpdate = () => {
        if (mutation == null) {
          return true;
        }
        if (mutation.timestamp > timestamp) {
          return false;
        }
        return true;
      };
      if (shouldUpdate()) {
        setMutation(generatedKey, {
          result: {
            data,
            status: "failure"
          },
          timestamp: (_a = mutation == null ? void 0 : mutation.timestamp) != null ? _a : Date.now(),
          isValidating: false
        });
      }
    });
    if (currentMutation && currentMutation.timestamp + fullOpts.freshAge + fullOpts.staleAge > timestamp) {
      currentMutation.timestamp = timestamp;
      currentMutation.isValidating = true;
      return currentMutation.result;
    }
    setMutation(generatedKey, {
      result,
      timestamp,
      isValidating: true
    });
    return result;
  };
  const cleanups = new Map();
  const lazyRegister = (...args) => {
    const generatedKey = fullOpts.key(...args);
    if (getMutationListenerSize(generatedKey) > 0) {
      return;
    }
    const currentCleanups = [];
    const subscription = (sub) => {
      currentCleanups.push(sub());
    };
    const onRevalidate = () => {
      setRevalidation(generatedKey, true);
    };
    subscription(() => {
      const innerRevalidate = (flag) => {
        revalidate(args, {
          shouldRevalidate: flag
        });
      };
      return subscribeRevalidation(generatedKey, innerRevalidate);
    });
    if (is_client_default) {
      if (fullOpts.refreshInterval != null) {
        if (fullOpts.refreshWhenBlurred) {
          subscription(() => {
            let interval;
            const enter = () => {
              window.clearInterval(interval);
              interval = window.setInterval(onRevalidate, fullOpts.refreshInterval);
            };
            const exit = () => {
              window.clearInterval(interval);
              interval = void 0;
            };
            window.addEventListener("blur", enter, false);
            window.addEventListener("focus", exit, false);
            return () => {
              window.removeEventListener("blur", enter, false);
              window.removeEventListener("focus", exit, false);
              window.clearInterval(interval);
            };
          });
        }
        if (fullOpts.refreshWhenOffline) {
          subscription(() => {
            let interval;
            const enter = () => {
              window.clearInterval(interval);
              interval = window.setInterval(onRevalidate, fullOpts.refreshInterval);
            };
            const exit = () => {
              window.clearInterval(interval);
              interval = void 0;
            };
            window.addEventListener("offline", enter, false);
            window.addEventListener("online", exit, false);
            return () => {
              window.removeEventListener("offline", enter, false);
              window.removeEventListener("online", exit, false);
              window.clearInterval(interval);
            };
          });
        }
        if (fullOpts.refreshWhenHidden) {
          subscription(() => {
            let interval;
            const onVisibility = () => {
              window.clearInterval(interval);
              if (document.visibilityState === "visible") {
                interval = void 0;
              } else {
                interval = window.setInterval(onRevalidate, fullOpts.refreshInterval);
              }
            };
            document.addEventListener("visibilitychange", onVisibility, false);
            return () => {
              document.removeEventListener("visibilitychange", onVisibility, false);
              window.clearInterval(interval);
            };
          });
        }
        if (!(fullOpts.refreshWhenHidden || fullOpts.refreshWhenBlurred || fullOpts.refreshWhenOffline)) {
          subscription(() => {
            const interval = window.setInterval(onRevalidate, fullOpts.refreshInterval);
            return () => {
              window.clearInterval(interval);
            };
          });
        }
      }
      if (options.revalidateOnFocus) {
        subscription(() => {
          window.addEventListener("focus", onRevalidate, false);
          return () => {
            window.removeEventListener("focus", onRevalidate, false);
          };
        });
      }
      if (options.revalidateOnNetwork) {
        subscription(() => {
          window.addEventListener("online", onRevalidate, false);
          return () => {
            window.removeEventListener("online", onRevalidate, false);
          };
        });
      }
      if (options.revalidateOnVisibility) {
        subscription(() => {
          const onVisible = () => {
            if (document.visibilityState === "visible") {
              onRevalidate();
            }
          };
          window.addEventListener("visibilitychange", onVisible, false);
          return () => {
            window.removeEventListener("visibilitychange", onVisible, false);
          };
        });
      }
    }
    cleanups.set(generatedKey, currentCleanups);
  };
  const lazyUnregister = (generatedKey) => {
    var _a;
    if (getMutationListenerSize(generatedKey) === 0) {
      (_a = cleanups.get(generatedKey)) == null ? void 0 : _a.forEach((cleanup) => {
        cleanup();
      });
      cleanups.delete(generatedKey);
    }
  };
  return {
    id: `SWRStore-${getIndex()}`,
    trigger: (args, shouldRevalidate = true) => {
      const generatedKey = fullOpts.key(...args);
      trigger(generatedKey, shouldRevalidate);
    },
    mutate: (args, data, shouldRevalidate = true, compare = fullOpts.compare) => {
      const generatedKey = fullOpts.key(...args);
      mutate(generatedKey, data, shouldRevalidate, compare);
    },
    get: revalidate,
    subscribe: (args, listener) => {
      const generatedKey = fullOpts.key(...args);
      lazyRegister(...args);
      const unsubscribe = subscribe(generatedKey, listener);
      return () => {
        unsubscribe();
        lazyUnregister(generatedKey);
      };
    }
  };
}
export {
  createSWRStore,
  mutate,
  subscribe,
  trigger
};
/**
 * @license
 * MIT License
 *
 * Copyright (c) 2021 Alexis Munsayac
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 *
 * @author Alexis Munsayac <alexis.munsayac@gmail.com>
 * @copyright Alexis Munsayac 2021
 */
//# sourceMappingURL=index.js.map
